import React from 'react';
import { determineMaxSavings } from '@/components/account/NextPlayKit/utils';
import { constructLegacyCustomerPortalLink } from '@/utils/shopSubdomain';
import {
  buildBannerPropsWithDynamicContent,
  trackClickEvent,
  TypeScheduledItemGroup,
} from '@/components/account/PlayKitSchedule/utils';
import { TypePlayKitBundleOffer } from '@/lib/api/productCatalog/types';
import { TypeBanner } from '@/core/generated-types';
import useFeature from '@/hooks/useFeature';
import useLocale from '@/hooks/useLocale';
import { CmsBanner } from '@/components/generics/GenBanner';
import { CTA_NAMES } from '@/utils/rudderstack';

export type PrepayUpsellBannersProps = {
  group: TypeScheduledItemGroup;
  prepayBundleExtensionOffers: TypePlayKitBundleOffer[];
  prepayBanner?: TypeBanner;
  holidayBanner?: TypeBanner;
};

function PrepayUpsellBanners({
  group,
  prepayBundleExtensionOffers,
  prepayBanner,
  holidayBanner,
}: PrepayUpsellBannersProps) {
  const isBfcm2024Enabled = useFeature('money_enable-bfcm-2024');
  const locale = useLocale();

  const groupHasNextItemEligibleForPrepayExtension = group.items.some(
    (item) => item.isNextItemEligibleForPrepayExtension,
  );

  const isEligibleForPrepayUpsell =
    !isBfcm2024Enabled &&
    !group.isRecurringPufSub &&
    !group.isPartialSub &&
    groupHasNextItemEligibleForPrepayExtension &&
    prepayBundleExtensionOffers.length > 0;
  const isEligibleForHolidayUpsell =
    isBfcm2024Enabled &&
    !group.isPartialSub &&
    groupHasNextItemEligibleForPrepayExtension &&
    prepayBundleExtensionOffers.length > 0;

  if (!isEligibleForPrepayUpsell && !isEligibleForHolidayUpsell) {
    return null;
  }

  const maxSavings = determineMaxSavings(prepayBundleExtensionOffers);
  const prepayExtensionFlowLink = constructLegacyCustomerPortalLink(locale, [
    group.userUuid,
    'subscriptions',
    group.subscriptionUuid,
    'prepay-extension',
  ]);
  const prepayAndSaveBannerProps =
    prepayBanner &&
    buildBannerPropsWithDynamicContent(prepayBanner.fields, {
      linkUrl: prepayExtensionFlowLink,
      headingStringInterpolations: { savings: maxSavings },
    });

  const holidayBannerProps =
    holidayBanner &&
    buildBannerPropsWithDynamicContent(holidayBanner.fields, {
      linkUrl: prepayExtensionFlowLink,
    });

  return (
    <>
      {isEligibleForPrepayUpsell && prepayAndSaveBannerProps && (
        <CmsBanner
          testId="prepay-extension-banner"
          {...prepayAndSaveBannerProps}
          classes="mb-6"
          onClickCta={() =>
            trackClickEvent(CTA_NAMES.PREPAY_EXTENSION_FLOW_STARTED)
          }
        />
      )}
      {isEligibleForHolidayUpsell && holidayBannerProps && (
        <CmsBanner
          testId="prepay-extension-holiday-banner"
          {...holidayBannerProps}
          classes="mb-6"
          onClickCta={() =>
            trackClickEvent(CTA_NAMES.PREPAY_EXTENSION_FLOW_STARTED)
          }
        />
      )}
    </>
  );
}

export default PrepayUpsellBanners;
